import type { ComponentType, ReactNode } from "react";
import {
  SearchFilterToolbarShell,
  searchFilterToolbarDefaultClassNames,
  type SearchFilterToolbarClassNamesOverride,
} from "../shared/SearchFilterToolbarShell";
import { JobSearchFilterBadges } from "./JobSearchFilterBadges";
import type { CityLike, ServiceLike } from "./JobSearchFilterBadges";
import type {
  AlphabeticalSortOption,
  JobSearchFilterToolbarLabels,
  ModalSlotRenderProps,
  PublishedAtSortOption,
  ToolbarBadgeProps,
  ToolbarButtonProps,
} from "./types";

export type { CityLike, ServiceLike };

export const jobSearchFilterToolbarDefaultClassNames =
  searchFilterToolbarDefaultClassNames;

export type JobSearchFilterToolbarClassNames =
  SearchFilterToolbarClassNamesOverride & {
    badgeRow?: string;
    badgeFilters?: string;
    badgeSorts?: string;
  };

export type JobSearchFilterToolbarProps<
  TService extends ServiceLike,
  TCity extends CityLike,
> = {
  onApplySearch: () => void | Promise<void>;
  labels: JobSearchFilterToolbarLabels;
  getLanguageDisplayLabel: (code: string) => string;

  selectedLanguages: string[];
  selectedServices: TService[];
  selectedCities: TCity[];
  alphabeticalSort: AlphabeticalSortOption | null;
  publishedAtSort: PublishedAtSortOption | null;

  onRemoveLanguage: (code: string) => void | Promise<void>;
  onRemoveService: (id: number) => void | Promise<void>;
  onRemoveCity: (id: number) => void | Promise<void>;
  onRemoveAlphabeticalSortBadge: () => void | Promise<void>;
  onRemovePublishedAtSortBadge: () => void | Promise<void>;

  components: {
    Button: ComponentType<ToolbarButtonProps>;
    Badge: ComponentType<ToolbarBadgeProps>;
  };

  renderFilterModal: (props: ModalSlotRenderProps) => ReactNode;
  renderSortModal: (props: ModalSlotRenderProps) => ReactNode;

  classNames?: JobSearchFilterToolbarClassNames;

  renderToolbarAccessory?: (ctx: { desktopModalsOpen: boolean }) => ReactNode;
};

export function JobSearchFilterToolbar<
  TService extends ServiceLike,
  TCity extends CityLike,
>({
  onApplySearch,
  labels,
  getLanguageDisplayLabel,
  selectedLanguages,
  selectedServices,
  selectedCities,
  alphabeticalSort,
  publishedAtSort,
  onRemoveLanguage,
  onRemoveService,
  onRemoveCity,
  onRemoveAlphabeticalSortBadge,
  onRemovePublishedAtSortBadge,
  components: { Button, Badge },
  renderFilterModal,
  renderSortModal,
  classNames: classNamesProp,
  renderToolbarAccessory,
}: JobSearchFilterToolbarProps<TService, TCity>) {
  const { badgeRow, badgeFilters, badgeSorts, ...toolbarClassNames } =
    classNamesProp ?? {};

  return (
    <>
      <SearchFilterToolbarShell
        labels={{ filterBy: labels.filterBy, sortBy: labels.sortBy }}
        classNames={toolbarClassNames}
        Button={Button}
        renderFilterModal={renderFilterModal}
        renderSortModal={renderSortModal}
        onApplySearch={onApplySearch}
        renderBetween={renderToolbarAccessory}
        exclusiveMobilePanels
        closeDesktopOnOutsideClick
      />

      <JobSearchFilterBadges
        labels={labels}
        getLanguageDisplayLabel={getLanguageDisplayLabel}
        selectedLanguages={selectedLanguages}
        selectedServices={selectedServices}
        selectedCities={selectedCities}
        alphabeticalSort={alphabeticalSort}
        publishedAtSort={publishedAtSort}
        onRemoveLanguage={onRemoveLanguage}
        onRemoveService={onRemoveService}
        onRemoveCity={onRemoveCity}
        onRemoveAlphabeticalSortBadge={onRemoveAlphabeticalSortBadge}
        onRemovePublishedAtSortBadge={onRemovePublishedAtSortBadge}
        Badge={Badge}
        badgeRowClassName={badgeRow}
        badgeFiltersClassName={badgeFilters}
        badgeSortsClassName={badgeSorts}
      />
    </>
  );
}
